import { ProcedureReadinessPreview } from './ProcedureReadinessPreview';
import { useContext, useState } from 'react';
import { T, useGT, Var } from 'gt-react';
import {
  CaretDownIcon,
  CaretUpIcon,
  PlusIcon,
  TrashIcon,
} from '@phosphor-icons/react';
import {
  TASK_PROCEDURE_PHASES,
  type TaskProcedurePhase,
} from '@erp/shared-types';
import type { ProcedureHealthResponse } from '@erp/rpc';
import { Button } from 'src/components/ui/button';
import { Input } from 'src/components/ui/input';
import { Label } from 'src/components/ui/label';
import { Badge } from 'src/components/ui/badge';
import { AlertContainer, AlertDescription } from 'src/components/ui/alert';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'src/components/ui/select';
import { AuthContext } from 'src/context/auth.context';
import { useSopVersions } from 'src/api/rpc/hooks/agent/useOperatorSops';
import { SopSelect } from 'src/pages/authenticated/agents/components/SopSelect';
import { ProcedureParameterMapper } from './ProcedureParameterMapper';
import { TaskTypeSection } from './TaskTypeEditorSections';
import type {
  ProcedureInputField,
  ProcedureInputSchemaStatus,
} from './procedureParameterBinding';
import {
  newProcedureDraft,
  parseProcedureDrafts,
  PROCEDURE_WHEN_TO_USE_MAX,
  type ProcedureDraftErrorCode,
  type TaskTypeProcedureDraft,
} from './taskTypeAdmin';
import {
  describeProcedureHealth,
  type ProcedureHealthSummary,
} from './taskTypeProcedureHealth';

const FOLLOW_PUBLISHED_VALUE = '__follow_published__';
/** The two editable shelves, in display order. */
const LANE_MODES = ['required', 'suggested'] as const;

/**
 * The task type's playbook: two lanes of SOP bindings. Required procedures
 * are briefed in full to the agent and must be engaged before the task
 * completes; suggested procedures are disclosed as a one-line hint and read
 * on demand. Order within a lane is the order the agent sees.
 */
export function TaskTypeProceduresEditor({
  drafts,
  disabled,
  onChange,
  health,
  agentId,
  inputFields = [],
  schemaStatus = 'none',
}: {
  agentId?: string;
  drafts: TaskTypeProcedureDraft[];
  disabled?: boolean;
  onChange: (next: TaskTypeProcedureDraft[]) => void;
  /** Read-only health over recent tasks (existing types only). */
  health?: ProcedureHealthResponse | null;
  /** Top-level fields of the type's input schema. Empty when none is selected. */
  inputFields?: readonly ProcedureInputField[];
  schemaStatus?: ProcedureInputSchemaStatus;
}) {
  const parsed = parseProcedureDrafts(drafts);
  const error = parsed.ok ? null : parsed.error;

  function updateDraft(
    clientId: string,
    patch: Partial<TaskTypeProcedureDraft>,
  ) {
    onChange(
      drafts.map((draft) =>
        draft.clientId === clientId ? { ...draft, ...patch } : draft,
      ),
    );
  }

  function moveWithinLane(clientId: string, direction: -1 | 1) {
    const lane = drafts.filter(
      (d) => d.mode === drafts.find((x) => x.clientId === clientId)?.mode,
    );
    const laneIndex = lane.findIndex((d) => d.clientId === clientId);
    const swapWith = lane[laneIndex + direction];
    if (!swapWith) return;
    const a = drafts.findIndex((d) => d.clientId === clientId);
    const b = drafts.findIndex((d) => d.clientId === swapWith.clientId);
    const next = [...drafts];
    const current = next[a];
    const other = next[b];
    if (!current || !other) return;
    next[a] = other;
    next[b] = current;
    onChange(next);
  }

  return (
    <TaskTypeSection
      title={<T context="task type editor section title">Procedures</T>}
      description={
        <T context="task type editor procedures section description">
          The playbook an agent gets for tasks of this type. Required procedures
          are named in the briefing and the agent must read or replay them
          during task execution before reporting completion; suggested ones are
          offered as a one-line hint the agent opens only when it applies.
        </T>
      }
    >
      {agentId && parsed.ok && !disabled ? (
        <ProcedureReadinessPreview
          agentId={agentId}
          procedures={parsed.procedures}
        />
      ) : (
        <p className="text-sm text-muted-foreground">
          <T context="procedure availability for variable assignees">
            Availability is checked for the assigned agent on each task.
          </T>
        </p>
      )}
      {health ? (
        <p className="text-xs text-muted-foreground">
          <T context="task procedure health sample and current binding attribution">
            Health covers <Var>{health.tasksInWindow}</Var> recent tasks using
            this type's current bindings, not historical binding snapshots.
          </T>
          {health.ambiguousPulls > 0 ? (
            <T context="task procedure health warning for ambiguous procedure titles">
              {' '}
              <Var>{health.ambiguousPulls}</Var> replay call(s) were omitted
              because multiple procedures share their title.
            </T>
          ) : null}
          {health.truncated ? (
            <T context="task procedure health sampling limit warning">
              {' '}
              Sampling limits were reached; some tasks or procedure activity are
              omitted.
            </T>
          ) : null}
        </p>
      ) : null}
      {LANE_MODES.map((mode) => (
        <ProcedureLane
          key={mode}
          mode={mode}
          drafts={drafts}
          disabled={disabled}
          error={error}
          health={health}
          onUpdate={updateDraft}
          onMove={moveWithinLane}
          onRemove={(clientId) =>
            onChange(drafts.filter((d) => d.clientId !== clientId))
          }
          onAdd={() => onChange([...drafts, newProcedureDraft({ mode })])}
          inputFields={inputFields}
          schemaStatus={schemaStatus}
        />
      ))}
      {health && health.unboundPulls.length > 0 ? (
        <AlertContainer>
          <AlertDescription>
            <T context="task type procedures: agents pulled unbound procedures">
              In the last <Var>{health.days}</Var> days agents pulled procedures
              this type does not bind:{' '}
              <Var>
                {health.unboundPulls
                  .slice(0, 5)
                  .map((u) => `${u.name} (${u.tasksWithPull})`)
                  .join(', ')}
              </Var>
              . Consider adding them as suggested.
            </T>
          </AlertDescription>
        </AlertContainer>
      ) : null}
    </TaskTypeSection>
  );
}

/** Edit the ordered bindings for one requirement level while preserving the other lane. */
function ProcedureLane({
  mode,
  drafts,
  disabled,
  error,
  health,
  onUpdate,
  onMove,
  onRemove,
  onAdd,
  inputFields,
  schemaStatus,
}: {
  mode: TaskTypeProcedureDraft['mode'];
  drafts: TaskTypeProcedureDraft[];
  disabled?: boolean;
  error: { code: ProcedureDraftErrorCode; index: number } | null;
  health?: ProcedureHealthResponse | null;
  onUpdate: (clientId: string, patch: Partial<TaskTypeProcedureDraft>) => void;
  onMove: (clientId: string, direction: -1 | 1) => void;
  onRemove: (clientId: string) => void;
  onAdd: () => void;
  inputFields: readonly ProcedureInputField[];
  schemaStatus: ProcedureInputSchemaStatus;
}) {
  const gt = useGT();
  const lane = drafts.filter((d) => d.mode === mode);
  const healthBySop = new Map(
    (health?.entries ?? []).map((entry) => [entry.sopId, entry]),
  );
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between">
        <Label className="text-[11px] uppercase tracking-wide text-muted-foreground">
          {mode === 'required' ? (
            <T context="task type procedures lane heading">Required</T>
          ) : (
            <T context="task type procedures lane heading">Suggested</T>
          )}
        </Label>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={disabled}
          onClick={onAdd}
        >
          <PlusIcon className="size-3.5" />
          {mode === 'required' ? (
            <T context="button, add a required procedure to a task type">
              Add Required
            </T>
          ) : (
            <T context="button, add a suggested procedure to a task type">
              Add Suggested
            </T>
          )}
        </Button>
      </div>
      {lane.length === 0 ? (
        <p className="text-xs text-muted-foreground">
          {mode === 'required' ? (
            <T context="empty state, no required procedures on a task type">
              No prescribed procedures. Agents work from the task description
              alone.
            </T>
          ) : (
            <T context="empty state, no suggested procedures on a task type">
              No hints. Anything else in the agent&apos;s allowlist stays in its
              library.
            </T>
          )}
        </p>
      ) : (
        lane.map((draft, laneIndex) => {
          const overallIndex = drafts.findIndex(
            (d) => d.clientId === draft.clientId,
          );
          return (
            <ProcedureRow
              key={draft.clientId}
              draft={draft}
              disabled={disabled}
              isFirst={laneIndex === 0}
              isLast={laneIndex === lane.length - 1}
              errorCode={error?.index === overallIndex ? error.code : null}
              health={describeProcedureHealth(healthBySop.get(draft.sopId))}
              onUpdate={(patch) => onUpdate(draft.clientId, patch)}
              onMove={(direction) => onMove(draft.clientId, direction)}
              onRemove={() => onRemove(draft.clientId)}
              inputFields={inputFields}
              schemaStatus={schemaStatus}
              gt={gt}
            />
          );
        })
      )}
    </div>
  );
}

/** Edit one procedure binding, including when it runs, its version, notes, and parameters. */
function ProcedureRow({
  draft,
  disabled,
  isFirst,
  isLast,
  errorCode,
  health,
  onUpdate,
  onMove,
  onRemove,
  inputFields,
  schemaStatus,
  gt,
}: {
  draft: TaskTypeProcedureDraft;
  disabled?: boolean;
  isFirst: boolean;
  isLast: boolean;
  errorCode: ProcedureDraftErrorCode | null;
  health: ProcedureHealthSummary | null;
  onUpdate: (patch: Partial<TaskTypeProcedureDraft>) => void;
  onMove: (direction: -1 | 1) => void;
  onRemove: () => void;
  inputFields: readonly ProcedureInputField[];
  schemaStatus: ProcedureInputSchemaStatus;
  gt: ReturnType<typeof useGT>;
}) {
  const [paramsOpen, setParamsOpen] = useState(draft.params.length > 0);
  const isRequired = draft.mode === 'required';
  return (
    <div className="flex flex-col gap-2 rounded-md border border-border p-3">
      <div className="flex items-start gap-2">
        <div className="flex flex-col">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="size-6"
            disabled={disabled || isFirst}
            aria-label={gt('Move procedure up', {
              context: 'icon button reordering a task type procedure',
            })}
            onClick={() => onMove(-1)}
          >
            <CaretUpIcon className="size-3.5" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="size-6"
            disabled={disabled || isLast}
            aria-label={gt('Move procedure down', {
              context: 'icon button reordering a task type procedure',
            })}
            onClick={() => onMove(1)}
          >
            <CaretDownIcon className="size-3.5" />
          </Button>
        </div>
        <div className="flex min-w-0 flex-1 flex-col gap-2">
          <div className="grid grid-cols-1 gap-2 md:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
            <div className="space-y-1">
              <Label className="text-[11px]">
                <T context="task type procedure row field label">Procedure</T>
              </Label>
              <SopSelect
                value={draft.sopId}
                disabled={disabled}
                onChange={(sopId) => onUpdate({ sopId, pinnedVersionId: null })}
              />
            </div>
            <ProcedureVersionSelect
              sopId={draft.sopId}
              value={draft.pinnedVersionId}
              disabled={disabled}
              onChange={(pinnedVersionId) => onUpdate({ pinnedVersionId })}
            />
          </div>
          <ProcedureExecutionField
            draft={draft}
            disabled={disabled}
            onUpdate={onUpdate}
            gt={gt}
          />
          <ProcedureHintField
            draft={draft}
            isRequired={isRequired}
            disabled={disabled}
            onUpdate={onUpdate}
            gt={gt}
          />
          <div className="flex flex-wrap items-center gap-2">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-7 px-2 text-xs"
              disabled={disabled}
              onClick={() => setParamsOpen((open) => !open)}
            >
              <T context="toggle, parameter bindings of a task type procedure">
                Parameters (<Var>{draft.params.length}</Var>)
              </T>
            </Button>
            {health ? <ProcedureHealthChips health={health} /> : null}
          </div>
          {paramsOpen ? (
            <ProcedureParameterMapper
              draft={draft}
              disabled={disabled}
              fields={inputFields}
              schemaStatus={schemaStatus}
              onUpdate={onUpdate}
            />
          ) : null}
          {errorCode ? (
            <p className="text-xs text-destructive">
              {procedureErrorMessage(errorCode, gt)}
            </p>
          ) : null}
        </div>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="size-7"
          disabled={disabled}
          aria-label={gt('Remove procedure', {
            context: 'icon button removing a task type procedure binding',
          })}
          onClick={onRemove}
        >
          <TrashIcon className="size-3.5" />
        </Button>
      </div>
    </div>
  );
}

function ProcedureExecutionField({
  draft,
  disabled,
  onUpdate,
  gt,
}: {
  draft: TaskTypeProcedureDraft;
  disabled?: boolean;
  onUpdate: (patch: Partial<TaskTypeProcedureDraft>) => void;
  gt: ReturnType<typeof useGT>;
}) {
  return (
    <div className="flex flex-col gap-1">
      <Label>
        <T context="task procedure execution field">Execution</T>
      </Label>
      <Select
        value={draft.mode === 'suggested' ? 'suggested' : draft.phase}
        disabled={disabled}
        onValueChange={(value) => {
          const patch = executionPatch(value);
          if (patch) onUpdate(patch);
        }}
      >
        <SelectTrigger>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {TASK_PROCEDURE_PHASES.map((phase) => (
            <SelectItem key={phase} value={phase}>
              {phaseLabel(phase, gt)}
            </SelectItem>
          ))}
          <SelectItem value="suggested">
            <T context="procedure execution option">Suggested During Task</T>
          </SelectItem>
        </SelectContent>
      </Select>
    </div>
  );
}

function executionPatch(value: string): Partial<TaskTypeProcedureDraft> | null {
  if (value === 'suggested') return { mode: 'suggested', phase: 'main' };
  const phase = TASK_PROCEDURE_PHASES.find((candidate) => candidate === value);
  if (!phase) return null;
  return { mode: 'required', phase };
}

function ProcedureHintField({
  draft,
  isRequired,
  disabled,
  onUpdate,
  gt,
}: {
  draft: TaskTypeProcedureDraft;
  isRequired: boolean;
  disabled?: boolean;
  onUpdate: (patch: Partial<TaskTypeProcedureDraft>) => void;
  gt: ReturnType<typeof useGT>;
}) {
  return (
    <div className="space-y-1">
      <Label className="text-[11px]">
        {isRequired ? (
          <T context="task type procedure row field label">
            Note for the agent (optional)
          </T>
        ) : (
          <T context="task type procedure row field label">When to use</T>
        )}
      </Label>
      <Input
        value={draft.whenToUse}
        disabled={disabled}
        maxLength={PROCEDURE_WHEN_TO_USE_MAX}
        placeholder={procedureHintPlaceholder(isRequired, gt)}
        onChange={(event) => onUpdate({ whenToUse: event.target.value })}
      />
    </div>
  );
}

function procedureHintPlaceholder(
  isRequired: boolean,
  gt: ReturnType<typeof useGT>,
): string {
  if (isRequired) {
    return gt('e.g. Post before reconciling the batch', {
      context: 'placeholder, note on a required procedure',
    });
  }
  return gt('e.g. the receipt number already exists in Famous', {
    context: 'placeholder, when-to-use hint on a suggested procedure',
  });
}

/** Choose between the published procedure and a finalized pinned version. */
function ProcedureVersionSelect({
  sopId,
  value,
  disabled,
  onChange,
}: {
  sopId: string;
  value: string | null;
  disabled?: boolean;
  onChange: (pinnedVersionId: string | null) => void;
}) {
  const { currentUser } = useContext(AuthContext);
  const { data } = useSopVersions(
    currentUser?.organizationId,
    sopId || undefined,
  );
  const finalized = (data?.versions ?? []).filter(
    (version) => version.status === 'finalized',
  );
  return (
    <div className="space-y-1">
      <Label className="text-[11px]">
        <T context="task type procedure row field label">Version</T>
      </Label>
      <Select
        value={value ?? FOLLOW_PUBLISHED_VALUE}
        disabled={disabled || !sopId}
        onValueChange={(next) =>
          onChange(next === FOLLOW_PUBLISHED_VALUE ? null : next)
        }
      >
        <SelectTrigger className="h-8">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={FOLLOW_PUBLISHED_VALUE}>
            <T context="option, procedure binding follows the published SOP version">
              Follow published
            </T>
          </SelectItem>
          {finalized.map((version) => (
            <SelectItem key={version.id} value={version.id}>
              <T context="option, pin a procedure binding to one SOP version">
                Pin v<Var>{version.version}</Var>
              </T>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

function ProcedureHealthChips({ health }: { health: ProcedureHealthSummary }) {
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {health.runSuccessRate !== null ? (
        <Badge
          variant={health.runSuccessRate >= 0.9 ? 'outline' : 'warning'}
          className="text-[11px] font-normal"
        >
          <T context="chip, required procedure run success rate">
            <Var>{Math.round(health.runSuccessRate * 100)}</Var>% runs ok (
            <Var>{health.runsTotal}</Var>)
          </T>
        </Badge>
      ) : null}
      <Badge variant="outline" className="text-[11px] font-normal">
        <T context="chip, share of tasks where the agent pulled this procedure">
          read or replayed in <Var>{health.tasksWithPull}</Var> task
          <Var>{health.tasksWithPull === 1 ? '' : 's'}</Var>
        </T>
      </Badge>
      {health.waivers > 0 ? (
        <Badge variant="warning" className="text-[11px] font-normal">
          <T context="chip, human waivers of a required procedure">
            waived <Var>{health.waivers}</Var>×
          </T>
        </Badge>
      ) : null}
    </div>
  );
}

function phaseLabel(
  value: TaskProcedurePhase,
  gt: ReturnType<typeof useGT>,
): string {
  switch (value) {
    case 'setup':
      return gt('Run Before Task', {
        context: 'procedure phase option',
      });
    case 'main':
      return gt('Required During Task — Read or Replay', {
        context: 'procedure phase option',
      });
    case 'teardown':
      return gt('Run After Task', {
        context: 'procedure phase option',
      });
  }
}

function procedureErrorMessage(
  code: ProcedureDraftErrorCode,
  gt: ReturnType<typeof useGT>,
): string {
  switch (code) {
    case 'too_many_procedures':
      return gt('A playbook can contain at most 50 procedures.', {
        context: 'validation, task type procedure limit',
      });
    case 'invalid_binding':
      return gt('Check this procedure and its pinned version.', {
        context: 'validation, invalid task type procedure binding',
      });
    case 'missing_sop':
      return gt('Pick a procedure.', {
        context: 'validation, task type procedure row without a SOP',
      });
    case 'duplicate_sop':
      return gt('This procedure is already bound on this type.', {
        context: 'validation, duplicate SOP on a task type',
      });
    case 'duplicate_param':
      return gt('Each parameter can be bound once.', {
        context: 'validation, duplicate parameter binding',
      });
    case 'empty_param':
      return gt('Every parameter binding needs a parameter name.', {
        context: 'validation, empty parameter name',
      });
    case 'hint_too_long':
      return gt('Keep the hint under 280 characters.', {
        context: 'validation, when-to-use hint too long',
      });
  }
}
